import {
    ButtonGradientPrimary,
    ButtonSecondary,
    CheckboxGroup,
    Colors,
    Icon,
    ModalDialog,
    type CheckboxGroupOption,
} from '@amelio/ui-library';
import { useState } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';

interface AddCriteriaModalProps {
    isOpen: boolean;
    criteriaOptions: CheckboxGroupOption[];
    criteriaSelected: string[];
    onUpdateCriteria: (criteria: string[]) => void;
    onClose: () => void;
}

export const AddCriteriaModal = ({
    isOpen,
    criteriaOptions,
    criteriaSelected,
    onUpdateCriteria,
    onClose,
}: AddCriteriaModalProps) => {
    const { t } = useAmelioTranslations();
    const [selectedCriteria, setSelectedCriteria] = useState<string[]>(criteriaSelected);
    const [wasOpen, setWasOpen] = useState(isOpen);

    // Every opening starts from the criteria already on the board: a selection left unconfirmed
    // the last time must not come back. Only the opening resets it, so a parent handing down a new
    // array while the dialog is open keeps the criteria being picked.
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);

        if (isOpen) {
            setSelectedCriteria(criteriaSelected);
        }
    }

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('cycleEvaluationSelectEvaluators.addCriteriaModal.title')}
            titleSize={'24px'}
            size={'sm'}
            className={
                '[&_[data-testid=add-criteria-modal-close]]:bg-base-100 max-w-128 [&_[data-testid=add-criteria-modal-dialog]>div:last-child]:-mt-3 [&_[data-testid=add-criteria-modal-dialog]>div:last-child]:border-t-0'
            }
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={'add-criteria-modal'}
            footer={
                <div className={'flex justify-end gap-4'}>
                    <ButtonSecondary
                        size={'md'}
                        fitContent={true}
                        dataTestId={'add-criteria-cancel'}
                        onClick={onClose}
                        className={'min-w-21'}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        size={'md'}
                        fitContent={true}
                        className={'min-w-22.75'}
                        leftIcon={
                            <Icon
                                color={Colors.WHITE}
                                name={'essential-add'}
                                size={'md'}
                                ariaHidden
                            />
                        }
                        dataTestId={'add-criteria-confirm'}
                        onClick={() => {
                            onUpdateCriteria(selectedCriteria);
                            onClose();
                        }}
                    >
                        {t('common.add')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <CheckboxGroup
                options={criteriaOptions.map((option) => ({
                    ...option,
                    label: decodeAndSanitize(option.label),
                }))}
                name={'attributes'}
                onChange={setSelectedCriteria}
                values={selectedCriteria}
                size={'sm'}
                spacing={'tight'}
                className={
                    '[&_label]:text-body-sm [&_label]:text-navy mt-2 [&_label]:font-medium [&>div]:gap-0 [&>div>div]:min-h-12 [&>div>div]:justify-center [&>div>div]:rounded-lg [&>div>div]:px-3 [&>div>div]:transition-colors [&>div>div]:hover:bg-slate-50 [&>div>div:has(input[value=SameDepartment])]:order-3 [&>div>div:has(input[value=SameEmployeeLevel])]:order-5 [&>div>div:has(input[value=SameJobTitle])]:order-1 [&>div>div:has(input[value=SameManager])]:order-2 [&>div>div:has(input[value=SameOffice])]:order-4 [&>div>div:not(:has(input:checked))]:opacity-40 [&>div>div>div]:gap-3'
                }
            />
        </ModalDialog>
    );
};
